import { decrypt, encrypt } from '../../components/utils/symCrypto'
import VaultConfig from './config'
import { RequestHandler } from '../../components/utils/utils'
import { VaultClient } from '../../vault-client/client'
import { VaultWalletManager } from '../../vault-client/wallet'
import LANG from '../../components/utils/lang/en'
const bip39 = require('bip39')
const { VUE_APP_VAULT_KMS_SERVER_BASE_URL, VUE_APP_VAULT_EDV_SERVER_BASE_URL } = VaultConfig
const AUTH_SERVER_TOKEN_REFRESHED_STATUS = 304

function getAuthExpirationDate(yearsFromNow = 1) {
  const date = new Date()
  date.setUTCFullYear(date.getUTCFullYear() + yearsFromNow)
  return date.toISOString()
}

export default {
  async initializeVault({ commit, getters }, payload) {
    let { vaultId } = payload || {}
    if (!vaultId) {
      vaultId = getters.getVaultId
    }
    const vaultWallet = getters.getVaultWallet
    if (!vaultWallet) throw new Error('VaultWallet not provided')
    const vault = new VaultClient(vaultWallet, {
      edvId: vaultId,
      edvUrl: VUE_APP_VAULT_EDV_SERVER_BASE_URL,
    })
    await vault.Init()
    commit('setVault', vault)
  },

  async intitalizeVaultWallet({ commit }, payload) {
    const { mnemonic, namespace } = payload
    const vaultWallet = await VaultWalletManager.getWallet(mnemonic, namespace)
    commit('setVaultWallet', vaultWallet)
  },

  registerUser: ({ commit, getters, dispatch }) => {
    // eslint-disable-next-line
    return new Promise(async (resolve, reject) => {
      const url = `${VUE_APP_VAULT_KMS_SERVER_BASE_URL}/auth`
      const headers = {
        'content-type': 'application/json',
      }
      // const { email, name, accessToken } = getters.getProfile

      const { code, redirectUrl } = getters.getThirdPartyAuth

      // generate new mnemonic
      const mnemonic = bip39.generateMnemonic(256)
      commit('setVaultMnemonic', mnemonic)

      await dispatch('intitalizeVaultWallet', { mnemonic })

      const did = getters.getVaultWallet.didDocument.id
      // store at authserver
      return fetch(url, {
        method: 'POST',
        cache: 'no-store',
        headers,
        body: JSON.stringify({
          user: {
            did,
          },
          isThridPartyAuth: true,
          expirationDate: getAuthExpirationDate(1),
          authProvider: 'Google',
          code,
          redirectUrl,
        }),
      })
        .then((response) => response.json())
        .then(async (json) => {
          if (json) {
            if (json.error) {
              return reject(json.error)
            }

            if (json) {
              const authToken = json.message?.authToken || json.authToken
              if (!authToken) {
                return reject(new Error('Auth token missing from registration response'))
              }
              await commit('setAuthServerAuthToken', authToken)
              // Session was cleared by beforeunload during the OAuth redirect.
              // Create a fresh one before calling authenticateKYC.
              await dispatch('getNewSession')
              await dispatch('authenticateKYC')
              commit('setProfile', { name: json?.message?.name || '', email: json?.message?.email || '', did: json?.message?.did || '' })
              const { kmsIds, vaultId, salt } = json.message
              if (kmsIds && kmsIds.length > 0) {
                await dispatch('getKMSById', { kmsId: kmsIds[0] })
              }

              if (vaultId) {
                await commit('setVaultId', vaultId)
              }

              if (salt) {
                commit('setVaultSalt', salt)
              }

              if (json.status === 304) {
                commit('setAsNewUser', false)
              } else {
                commit('setAsNewUser', true)
              }
            }

            resolve(json)
          }
        })
        .catch((e) => {
          console.error('registerUser:: Error:', e)
          const errorMessage = e.message || e.toString()
          reject(new Error(`${LANG.AUTH.REGISTRATION_FAILED}: ${errorMessage}`))
        })
    })
  },
  // Called on every page reload when authServerAuthToken exists in localStorage.
  // Re-authenticates the existing user with the auth server to get a fresh token.
  // Does NOT generate a new mnemonic or touch vault data — only refreshes the token.
  refreshAuthServerToken: async ({ commit, getters, dispatch }, payload = {}) => {
    const url = `${VUE_APP_VAULT_KMS_SERVER_BASE_URL}/auth`
    const headers = { 'content-type': 'application/json' }
    const user = payload.user || getters.getProfile
    const authServerAuthToken = payload.authServerAuthToken || getters.getAuthServerAuthToken
    const forceVaultReload = payload.forceVaultReload === true

    if (!user?.did) {
      throw new Error('User DID is required for Auth Server re-authentication')
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        user,
        authToken: authServerAuthToken,
        isThridPartyAuth: false,
        expirationDate: '2030-12-31T00:00:00.000Z',
        authProvider: 'HypersignAuthServer',
      }),
    })
    const json = await response.json()
    const tokenRefreshSucceeded = response.ok || response.status === AUTH_SERVER_TOKEN_REFRESHED_STATUS || json.status === AUTH_SERVER_TOKEN_REFRESHED_STATUS
    if (!tokenRefreshSucceeded || json.error) {
      throw new Error(json.error || json.message || 'Auth server re-authentication failed')
    }
    const authData = json.message && typeof json.message === 'object' ? json.message : json
    const authToken = authData.authToken
    if (!authToken) {
      throw new Error('Auth token missing from re-authentication response')
    }
    commit('setAuthServerAuthToken', authToken)
    commit('setProfile', {
      name: authData.name || getters.getProfile?.name || '',
      email: authData.email || getters.getProfile?.email || '',
      did: authData.did || authData.user?.did || getters.getProfile?.did || '',
    })
    const { kmsIds, vaultId, salt } = authData
    if (kmsIds && kmsIds.length > 0) {
      // A mobile QR handoff must fetch this user's encrypted mnemonic instead
      // of trusting a value that may belong to an earlier browser session.
      if (forceVaultReload || !getters.getVaultData) {
        await dispatch('getKMSById', { kmsId: kmsIds[0] })
      }
      if (!getters.getVaultData) {
        throw new Error('Encrypted vault data was not returned by the Auth Server')
      }
      commit('setAsNewUser', false)
    } else {
      // No KMS set up yet — user registered but never completed PIN setup.
      // Show RegisterPIN so they can create their vault PIN.
      commit('setAsNewUser', true)
    }
    if (vaultId) {
      commit('setVaultId', vaultId)
    }
    if (salt) {
      commit('setVaultSalt', salt)
    }
  },

  registerAfterForgotPassword: ({ commit, getters, dispatch }, payload) => {
    // eslint-disable-next-line
    return new Promise(async (resolve, reject) => {
      const url = `${VUE_APP_VAULT_KMS_SERVER_BASE_URL}/auth`
      const headers = {
        'content-type': 'application/json',
      }
      // const { email, name, accessToken } = getters.getProfile

      // generate new mnemonic
      const mnemonic = bip39.generateMnemonic(256)
      commit('setVaultMnemonic', mnemonic)

      await dispatch('intitalizeVaultWallet', { mnemonic })

      const did = getters.getVaultWallet.didDocument.id
      // store at authserver
      return fetch(url, {
        method: 'POST',
        cache: 'no-store',
        headers,
        body: JSON.stringify({
          user: {
            did,
          },
          authToken: getters.getAuthServerAuthToken,
          isThridPartyAuth: false,
          expirationDate: getAuthExpirationDate(1),
          authProvider: 'HypersignAuthServer',
        }),
      })
        .then((response) => response.json())
        .then(async (json) => {
          if (json) {
            if (json.error) {
              return reject(json.error)
            }

            if (json) {
              const authToken = json.message?.authToken || json.authToken
              if (!authToken) {
                return reject(new Error('Auth token missing from forgot-password response'))
              }
              await commit('setAuthServerAuthToken', authToken)
              // Create a fresh session before calling authenticateKYC.
              await dispatch('getNewSession')
              await dispatch('authenticateKYC')
              commit('setProfile', { name: json?.message?.name || '', email: json?.message?.email || '', did: json?.message?.did || '' })
              const { kmsIds, vaultId, salt } = json.message
              if (kmsIds && kmsIds.length > 0) {
                await dispatch('getKMSById', { kmsId: kmsIds[0] })
              }

              if (vaultId) {
                await commit('setVaultId', vaultId)
              }

              if (salt) {
                commit('setVaultSalt', salt)
              }

              if (json.status === 304) {
                commit('setAsNewUser', false)
              } else {
                commit('setAsNewUser', true)
              }
            }

            resolve(json)
          }
        })
        .catch((e) => {
          console.error('registerUser:: Error:', e)
          const errorMessage = e.message || e.toString()
          reject(new Error(`${LANG.AUTH.REGISTRATION_FAILED}: ${errorMessage}`))
        })
    })
  },
  createKMS: async ({ getters, dispatch }) => {
    try {
      const vaultDataRaw = getters.getVaultDataRaw || {}
      const mnemonic_raw = getters.getVaultMnemonic || vaultDataRaw?.mnemonic || ''
      if (!mnemonic_raw || !bip39.validateMnemonic(mnemonic_raw)) {
        throw new Error('No valid key found to be stored on kms')
      }

      const vaultPin = getters.getVaultPin
      if (!vaultPin) {
        throw new Error('No PIN is set fo the vault')
      }

      const encryptedData = await encrypt(mnemonic_raw, vaultPin, getters.getVaultSalt)

      if (getters.getAuthServerAuthToken) {
        const url = `${VUE_APP_VAULT_KMS_SERVER_BASE_URL}/kms`
        const headers = {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + getters.getAuthServerAuthToken,
        }
        const body = {
          secret: encryptedData,
          did: getters.getVaultWallet.didDocument.id,
        }
        const resp = await RequestHandler(url, 'POST', body, headers, {})
        if (resp && resp.kmsId) {
          const { kmsId } = resp
          dispatch('getKMSById', { kmsId })
        }
        return resp
      } else {
        throw new Error('User is not authenticated with authserver')
      }
    } catch (e) {
      throw new Error(e.message)
    }
  },

  deleteAccount: async ({ getters }) => {
    try {
      if (getters.getAuthServerAuthToken) {
        const url = `${VUE_APP_VAULT_KMS_SERVER_BASE_URL}/user`
        const headers = {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + getters.getAuthServerAuthToken,
        }

        const resp = await RequestHandler(url, 'DELETE', {}, headers, {})
        return resp
      } else {
        throw new Error('User is not authenticated with authserver')
      }
    } catch (e) {
      throw new Error(e.message)
    }
  },

  getKMSById: async ({ getters, commit }, payload) => {
    try {
      const { kmsId } = payload
      if (!kmsId) {
        throw new Error('kmsId must be specified')
      }
      if (getters.getAuthServerAuthToken) {
        const url = `${VUE_APP_VAULT_KMS_SERVER_BASE_URL}/kms/${kmsId}`
        const headers = {
          'content-type': 'application/json',
          Authorization: 'Bearer ' + getters.getAuthServerAuthToken,
        }
        const resp = await RequestHandler(url, 'GET', {}, headers, {})
        // eslint-disable-next-line
        // debugger;
        const encryptedMnemonic = resp?.secret?.mnemonic || resp?.mnemonic
        if (!encryptedMnemonic) {
          throw new Error('Encrypted vault data is missing from the KMS response')
        }
        // This is encrypted and is decrypted only after the user enters PIN.
        commit('setVaultData', encryptedMnemonic)
        return resp
      }
    } catch (e) {
      throw new Error(e.message)
    }
  },

  getUserAccessMnemomic: async ({ getters, commit }) => {
    try {
      const vaultPin = getters.getVaultPin
      if (!vaultPin) {
        throw new Error('No vault pin specified')
      }
      const getEncrytedVaultMnemonic = getters.getVaultData
      const mnemonic = await decrypt(getEncrytedVaultMnemonic, vaultPin)
      await commit('setVaultMnemonic', mnemonic)
    } catch (e) {
      throw new Error(e.message)
    }
  },

  getDocumentIdsByNamespace: async ({ getters, commit }, payload) => {
    try {
      const vault = getters.getVault
      if (vault) {
        const queryResult = await vault.Query({
          equals: [
            {
              'content.metaData.namespace': payload.namespace,
            },
          ],
        })

        for (let i = 0; i < queryResult.length; i++) {
          const actualDoc = await vault.GetDecryptedDocument(queryResult[i].id)
          commit('updateVaultRawCredentials', [actualDoc.document])
        }
      } else {
        throw new Error('Vault has not been initialized')
      }
    } catch (e) {
      throw new Error(e.message)
    }
  },

  addUpdateDocumentById: async ({ getters }, payload) => {
    try {
      if (!payload) {
        throw new Error('Payload is required')
      }

      const { namespace, document } = payload
      if (!document) {
        throw new Error('Kindly pass document to add or update in edv')
      }

      const vault = getters.getVault
      if (vault) {
        const indexes = [
          {
            index: VaultConfig.VAULT_INDEX,
            unique: false,
          },
        ]
        const content = {
          document, // credential..
          metaData: {
            namespace,
          },
        }
        const preparedDoc = vault.PrepareEdvDocument(content, indexes)
        vault.CreateDocument({ preparedDoc })
      } else {
        throw new Error('Vault is not initialized')
      }
    } catch (e) {
      throw new Error(e.message)
    }
  },

  clearVaultAllData: async ({ getters }, payload) => {
    try {
      if (!payload) {
        // throw new Error('Payload is required')
      }
      const vault = getters.getVault
      if (vault) {
        // const queryResult = await vault.Query({
        //   equals: [
        //     {
        //       'content.metaData.namespace': payload.namespace,
        //     },
        //   ],
        // })

        // for (let i = 0; i < queryResult.length; i++) {
        //   vault.DeleteDocument(queryResult[i].id)
        // }
        vault.Delete()
      } else {
        throw new Error('Vault has not been initialized')
      }
    } catch (e) {
      throw new Error(e.message)
    }
  },

  retriveVaultCredentials: async ({ dispatch, getters }) => {
    try {
      const env = getters.getEnv
      const namespace = env === 'prod' ? VaultConfig.VAULT_NAMESPACE : `${VaultConfig.VAULT_NAMESPACE}-${env}`
      await dispatch('getDocumentIdsByNamespace', {
        namespace,
      })
    } catch (e) {
      throw new Error(e.message)
    }
  },

  async lockVault({ commit, getters }, payload = {}) {
    try {
      const vaultPin = getters.getVaultPin

      let vaultRaw = getters.getVaultDataRaw
      if (payload && Object.keys(payload).length > 0) {
        vaultRaw = payload
      }

      if (!vaultRaw) {
        return false
      }

      if (getters.getVaultLockStatus === true) {
        throw new Error('Vault is already locked')
      }

      const encryptedData = await encrypt(JSON.stringify(vaultRaw), vaultPin, getters.getVaultSalt)

      await commit('setVaultData', encryptedData)
      await commit('setVaultLockStatus', true)

      return true
    } catch (e) {
      throw new Error('Error: Could not lock vault')
    }
  },

  async unlockVault({ commit, getters }) {
    try {
      const vaultPin = getters.getVaultPin
      const vaultData = getters.getVaultData
      if (!vaultData) {
        return false
      }

      if (getters.getVaultLockStatus === false) {
        throw new Error('Vault is already unlocked')
      }
      if (!vaultData || vaultData === 'undefined') {
        return false
      }

      const decryptedData = await decrypt(vaultData, vaultPin)

      if (decryptedData === '') {
        throw new Error(LANG.VAULT.UNLOCK_FAILED)
      }

      if (decryptedData) {
        commit('setVaultRaw', decryptedData)
        commit('setVaultLockStatus', false)
      }

      return true
    } catch (e) {
      commit('clearVaultPin')
      console.error(e)
      throw new Error(LANG.VAULT.UNLOCK_FAILED)
    }
  },

  async updateVaultCredentials({ commit, getters, dispatch }, payload) {
    try {
      // check if vault is unlocked
      if (!payload) {
        throw new Error(LANG.VAULT.NO_CREDENTIAL_PAYLOAD)
      }

      commit('updateVaultRawCredentials', [payload])
      const isVaultEnabled = getters.getWidgetConfigFromDb?.isVaultEnabled ?? true
      if (isVaultEnabled) {
        if (!getters.getVaultPin) {
          throw new Error(LANG.VAULT.PIN_NOT_SET)
        }

        const env = getters.getEnv
        const namespace = env === 'prod' ? VaultConfig.VAULT_NAMESPACE : `${VaultConfig.VAULT_NAMESPACE}-${env}`
        const payload1 = {
          document: payload,
          namespace,
          metadata: 'credentials',
        }
        await dispatch('addUpdateDocumentById', payload1)
      }
    } catch (e) {
      console.error('updateVaultCredentials error:', e)
      throw new Error(`${LANG.VAULT.CREDENTIALS_UPDATE_FAILED}: ${e.message || e}`)
    }
  },

  checkIfCredentialAlreadyExistsInVault1({ commit, getters, state }, filter) {
    const { schemaType } = filter
    let trustedIssuerDidList = []
    const issuerDID = getters.getWidgetConfigFromDb.issuerDID
    if (issuerDID) {
      trustedIssuerDidList = issuerDID.split(',')
    }

    // Get vault credentials
    const credentials = getters.getVaultCredentials
    const credential = credentials.find((credential) => {
      if (credential) {
        if (credential.type.includes(schemaType) && trustedIssuerDidList.includes(credential.issuer)) {
          return credential
        }
      }
    })

    if (credential) {
      return true
    } else {
      return false
    }
  },

  async checkIfCredentialAlreadyExistsInVault({ commit, getters, state }) {
    const raw = localStorage.getItem(VaultConfig.LOCAL_STATES.VAULT_DATA_RAW)
    const vaultDataRaw = JSON.parse(raw)
    if (!vaultDataRaw) {
      return
    }

    const { hypersign } = vaultDataRaw
    const { credentials } = hypersign
    const { schemaIds } = state
    const issuerDID = getters.getWidgetConfigFromDb.issuerDID
    let trustedIssuerList = []
    if (issuerDID) {
      trustedIssuerList = issuerDID.split(',')
    }

    Object.keys(schemaIds).forEach((schema) => {
      const { schemaId } = schemaIds[schema]
      const credential = credentials.find((credential) => {
        if (credential) {
          // TODO: We can also add filter for trusted issuer later in the presentation request
          if (credential.credentialSchema?.id === schemaId && trustedIssuerList.includes(credential.issuer)) {
            return credential
          }
          if (credential.type.includes(schema) && trustedIssuerList.includes(credential.issuer)) {
            return credential
          }
        }
      })

      if (credential) {
        if (schema === 'PersonhoodCredential') {
          commit('setLivelinessCapturedData', credential.credentialSubject)
          commit('setLivelinessDone', true)
        }

        if (schema === 'PassportCredential') {
          commit('setKycDone', true)
        }

        if (schema === 'GovernmentIdCredential') {
          commit('setKycDone', true)
        }

        if (schema.includes('SBTCredential')) {
          commit('setSbtMintDone', true)
        }
      } else {
        // Credential not found for this schema and issuer combination
      }
    })
  },
}
